import { gql } from '@apollo/client';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
	InstallmentStatus,
	useUseInvoiceAutopay_GetPaymentMethodSessionSecretsQuery,
	useInvoiceAutopay_UpdateInvoiceAutopayMutation,
	DocumentAccessTokenDocumentType,
	UseInvoiceAutopayInvoiceFragment,
} from '#types';
import { useToast } from '../../../../hooks/useToast';
import { StripeElementsOptions } from '@stripe/stripe-js';
import rpcShared from '@rockpapercoin/rpc-shared';

export const useInvoiceAutopayInvoiceFragment = gql`
	fragment useInvoiceAutopayInvoice on Invoice {
		id
		contact {
			id
			customer {
				id
				stripeCustomerId
				clientUser {
					id
					plannerOrgCanPayInvoices
					assignedPlanner {
						id
						organization {
							id
							users {
								id
								user {
									id
								}
							}
						}
					}
				}
			}
		}
		paymentInstallments {
			id
			status
			scheduledPaymentDate
			scheduledPaymentMethod
		}
		organization {
			id
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const updateInvoiceAutopayMutation = gql`
	mutation InvoiceAutopay_updateInvoiceAutopay(
		$where: InvoiceWhereUniqueInput!
		$data: InvoiceAutoPayUpdateCustomInput!
	) {
		updateInvoiceAutoPay(where: $where, data: $data) {
			...useInvoiceAutopayInvoice
		}
	}
	${ useInvoiceAutopayInvoiceFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createPaymentMethodSessionQuery = gql`
	query useInvoiceAutopay_getPaymentMethodSessionSecrets(
		$where: PaymentMethodSessionSecretsInput
	) {
		getPaymentMethodSessionSecrets(where: $where) {
			clientSecret
			customerSessionClientSecret
		}
	}
`;

export const useInvoiceAutopay = ( {
	invoice,
	guestToken,
	requestingUserBaseId,
}: {
	invoice?: UseInvoiceAutopayInvoiceFragment;
	guestToken?: string;
	requestingUserBaseId?: string;
} ) => {
	const clientSecret = useMemo(
		() =>
			typeof window !== 'undefined'
				? new URLSearchParams( window.location.search ).get(
					'setup_intent_client_secret'
				  )
				: null,
		[]
	);
	const [ autopayIsExpanded, setAutopayIsExpanded ] = useState( !!clientSecret );
	const { showError, showSuccess } = useToast();

	const invoiceId = invoice?.id;
	const viewingUserIsCustomer = requestingUserBaseId
		? rpcShared.invoices.isCustomer( invoice, requestingUserBaseId )
		: false;
	const orgUser =
		invoice?.contact?.customer?.clientUser?.assignedPlanner?.organization?.users?.find(
			( user ) => user.user?.id === requestingUserBaseId
		);
	const viewingUserIsAPPA =
		requestingUserBaseId && invoice
			? rpcShared.invoices.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
				invoice,
				orgUser
			  )
			: false;
	const viewingUserIsAPPAWithPaymentPerms =
		viewingUserIsAPPA &&
		!!invoice?.contact?.customer?.clientUser?.plannerOrgCanPayInvoices;

	const viewingUserIsCustomerOrAPPAWithPaymentPerms =
		viewingUserIsCustomer || viewingUserIsAPPAWithPaymentPerms;

	const unpaidInstallments = invoice?.paymentInstallments?.filter(
		( installment ) => installment.status === InstallmentStatus.Unpaid
	);
	const autopayIsActive = unpaidInstallments?.every(
		( installment ) => installment.scheduledPaymentDate
	);
	const paymentMethodId = autopayIsActive
		? unpaidInstallments?.[ 0 ]?.scheduledPaymentMethod
		: undefined;

	const stripeCustomerId =
		viewingUserIsCustomerOrAPPAWithPaymentPerms && !viewingUserIsCustomer
			? invoice?.contact?.customer?.stripeCustomerId
			: undefined;

	const {
		data,
		loading: getPaymentMethodSessionSecretsLoading,
		error,
	} = useUseInvoiceAutopay_GetPaymentMethodSessionSecretsQuery( {
		variables: {
			where: {
				paymentMethodId,
				stripeCustomerId,
				organizationId: invoice?.organization?.id,
				...( guestToken && invoiceId
					? {
						token: {
							guestToken: guestToken,
							documentId: invoiceId,
							documentType: DocumentAccessTokenDocumentType.Invoice,
						},
					  }
					: {} ),
			},
		},
		skip: !autopayIsExpanded,
	} );

	const [ updateInvoiceAutopay, { loading: updateInvoiceAutopayLoading } ] =
		useInvoiceAutopay_UpdateInvoiceAutopayMutation();

	useEffect( () => {
		if ( error ) {
			showError( error.message );
		}
	}, [ error, showError ] );

	const stripeElementOptions: StripeElementsOptions | undefined = useMemo(
		() =>
			data?.getPaymentMethodSessionSecrets.clientSecret &&
			data?.getPaymentMethodSessionSecrets.customerSessionClientSecret
				? {
					clientSecret: data.getPaymentMethodSessionSecrets.clientSecret,
					customerSessionClientSecret:
							data.getPaymentMethodSessionSecrets.customerSessionClientSecret,
				  }
				: undefined,
		[ data ]
	);

	const updateAutopay = useCallback(
		async ( scheduledPaymentMethod?: string ) => {
			if ( !invoiceId ) {
				showError( 'Invoice id required in order to enable autopay' );
			} else {
				const res = await updateInvoiceAutopay( {
					variables: {
						where: {
							id: invoiceId,
						},
						data: {
							autoPay: !!scheduledPaymentMethod,
							scheduledPaymentMethod,
							...( guestToken && invoiceId
								? {
									token: {
										guestToken: guestToken,
										documentId: invoiceId,
										documentType: DocumentAccessTokenDocumentType.Invoice,
									},
								  }
								: {} ),
						},
					},
				} );
				if ( res.errors ) {
					res.errors.map( ( error ) => showError( error.message ) );
				} else {
					setAutopayIsExpanded( false );
					showSuccess( 'Autopay settings updated' );
				}
			}
		},
		[
			guestToken,
			invoiceId,
			showError,
			showSuccess,
			updateInvoiceAutopay
		]
	);

	const onDeactivate = useCallback(
		async ( e: React.FormEvent ) => {
			e.preventDefault();
			await updateAutopay();
		},
		[ updateAutopay ]
	);

	const onActivateOrUpdate = useCallback(
		async ( scheduledPaymentMethod: string ) => {
			await updateAutopay( scheduledPaymentMethod );
		},
		[ updateAutopay ]
	);

	return {
		loading:
			getPaymentMethodSessionSecretsLoading || updateInvoiceAutopayLoading,
		autopayIsActive,
		autopayIsExpanded,
		setAutopayIsExpanded,
		stripeElementOptions,
		onDeactivate,
		onActivateOrUpdate,
	};
};
